import {
    useState,
    SetStateAction,
    Dispatch,
    useEffect,
    CSSProperties,
} from "react";
import ImportFansTable from "./ImportFansTable";
import { STEPS } from "./UploadEmailsByCsv";
import useUploadEmailsByCsv from "Hooks/useUploadEmailsByCsv";
import CardSymphony from "elements/CardSymphony";
import Loader from "elements/Loader";
import Sty from "modules/Sty";
import ButtonSymphony from "elements/ButtonSymphony";

type PreviewData = {
    city: string;
    dateCollected: string;
    email: string;
    name: string;
    lastname: string;
    cellphone: string;
    state: string;
    country: string;
    source: string;
    tag: string;
    profile_url: string;
    custom_fields?: any;
};

interface FinalizeTabProps {
    selectedStep: STEPS;
    showError: boolean;
    setShowError: Dispatch<SetStateAction<boolean>>;
}

const FinalizeTab = ({
    selectedStep,
    showError,
    setShowError,
}: FinalizeTabProps) => {
    const {
        upload,
        isLoadingUploadEmails,
        getFansPreview
    } = useUploadEmailsByCsv();

    const { totalEmails } = upload;

    const { FINALIZE } = STEPS;

    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [fansPreviewData, setFansPreviewData] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);

    const getFansPreviewData = async (first = 1) => {
        if (first > 1) {
            setIsLoadingMore(true)
        }

        try {
            const { data, error } = await getFansPreview({
                page: 0,
                first: pageNumber,
            });

            // Extract data from rawData and flatten custom fields
            /** Example transformation:
             * Input: {
             *   name: "John",
             *   email: "john@email.com", 
             *   custom_fields: {
             *     favorite_color: "blue",
             *     membership_level: "gold"
             *   }
             * }
             * Output: {
             *   name: "John",
             *   email: "john@email.com",
             *   favorite_color: "blue", 
             *   membership_level: "gold"
             * }
             */
            const filteredData = data?.map((item: any) => {
                // Spread custom fields if they exist and remove custom_fields from base object
                const { custom_fields, ...baseFields } = item;
                const customFields = custom_fields ?
                    Object.entries(custom_fields).reduce((acc, [key, value]) => ({
                        ...acc,
                        [key]: value
                    }), {}) : {};

                // Return flattened object with base fields and custom fields
                return {
                    ...baseFields,
                    ...customFields
                };
            }) || [];

            if (error) {
                throw new Error("Error fetching fans preview data");
            }

            setFansPreviewData(
                filteredData.map(({ name, lastname, email, cellphone, source, city, state, country, profile_url, ...customFields }: PreviewData) => {
                    const randomColor = `#${Math.floor(Math.random() * 16777215).toString(
                        16
                    )}`;

                    return {
                        'Name': name,
                        'Last Name': lastname,
                        'Email': email,
                        'Cell Phone': cellphone,
                        'Source': source,
                        'City': city,
                        'State': state,
                        'Country': country,
                        ...customFields, // Spread all custom field key/value pairs directly
                        profileUrl: profile_url,
                        color: randomColor,
                    };
                })
            );
            setIsLoadingMore(false);
        }
        catch (error) {
            console.error(error);
        } finally {
            setIsLoadingMore(false);
        }
    };

    useEffect(() => {
        if (selectedStep === FINALIZE) {
            if (showError) {
                setShowError(false);
            }
            getFansPreviewData();
        }
    }, []);

    const handleClickLoadMore = () => {
        const pageNumberLocal = pageNumber + 1;
        getFansPreviewData(pageNumberLocal);
        setPageNumber(pageNumberLocal);
    };

    return (
        <div
            className="finalizeTabContainer"
            style={sty.finalizeTabContainer}
        >
            <CardSymphony
                className="finalizeTabHeader"
                title="Review your new fanbase"
                subtitle="Finalize your setup by reviewing the uploaded contacts and email details. Ensure everything looks correct before proceeding."
                size="small"
            />
            <CardSymphony
                className="finalizeTabImportText"
                title={`Import ${totalEmails} fans.`}
                subtitle="This new fanbase will be added to your current fanbase."
                size="small"
            />
            <CardSymphony
                className="finalizeTabTableContainer"
            >
                {isLoadingUploadEmails ?
                    <Loader
                        className="finalizeTabLoader"
                        size={44}
                        style={{ paddingTop: 48, paddingBottom: 48 }}
                        title="Loading fans..."
                    />
                    :
                    <ImportFansTable fansImportData={fansPreviewData} />
                }
            </CardSymphony>

            <div
                className="finalizeTabEmailCounterContainer"
                style={sty.finalizeTabEmailCounterContainer}
            >
                <div className="finalizeTabEmailCounter" style={Sty.text16grey}>
                    Showing {fansPreviewData.length} of {totalEmails} fans to be imported
                </div>
                {/* {fansPreviewData.length < totalEmails &&
                    <ButtonSymphony
                        className="finalizeTabLoadMoreButton"
                        isLoading={isLoadingMore}
                        disabled={isLoadingMore}
                        onClick={handleClickLoadMore}
                        loadingText="Loading more fans..."
                        variant="text"
                    >
                        Load more fans
                    </ButtonSymphony>
                } */}
            </div>
        </div>
    );
};

const sty: Record<string, CSSProperties> = {
    finalizeTabContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    finalizeTabEmailCounterContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        marginTop: 12,
    },
};

export default FinalizeTab;
